import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl, API_CONFIG } from '../config/api';
import MainNavbar from './MainNavbar';

const PagosDashboard = () => {
  const [alumnos, setAlumnos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtros, setFiltros] = useState({
    busqueda: '',
    categoria: '',
    curso: '',
    colegio: '',
    poblacion: ''
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchAlumnos();
    fetchCategorias();
  }, [navigate]);

  const fetchAlumnos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ALUMNOS), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/admin');
        return;
      }

      const data = await response.json();
      setAlumnos(data);
    } catch (error) {
      setError('Error cargando los datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategorias = async () => {
    try {
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.CATEGORIAS));
      const data = await response.json();
      setCategorias(data);
    } catch (error) {
      console.error('Error fetching categorias:', error);
    }
  };

  // Obtener valores únicos para los filtros
  const valoresUnicos = useMemo(() => {
    return {
      categorias: [...new Set(alumnos.map(a => a.categoria_nombre).filter(Boolean))].sort(),
      cursos: [...new Set(alumnos.map(a => a.curso).filter(Boolean))].sort(),
      colegios: [...new Set(alumnos.map(a => a.colegio).filter(Boolean))].sort(),
      poblaciones: [...new Set(alumnos.map(a => a.poblacion).filter(Boolean))].sort()
    };
  }, [alumnos]);

  // Orden lógico de categorías
  const ORDEN_CATEGORIAS = ['minis', 'prebenjamin', 'benjamin', 'alevin', 'infantil', 'cadete'];

  const indiceCategoria = (nombreCat) => {
    const normalizado = (nombreCat || '').toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const idx = ORDEN_CATEGORIAS.findIndex(c => normalizado.startsWith(c));
    return idx === -1 ? ORDEN_CATEGORIAS.length : idx;
  };

  // Filtrar y ordenar alumnos: por categoría y luego alfabéticamente (apellidos, nombre)
  const alumnosFiltrados = useMemo(() => {
    const filtrados = alumnos.filter(alumno => {
      // Filtro de búsqueda general
      if (filtros.busqueda) {
        const busqueda = filtros.busqueda.toLowerCase();
        const textoCompleto = `${alumno.nombre} ${alumno.apellidos} ${alumno.dni} ${alumno.padre_nombre} ${alumno.padre_apellidos} ${alumno.padre_telefono}`.toLowerCase();
        if (!textoCompleto.includes(busqueda)) return false;
      }

      // Filtros específicos
      if (filtros.categoria && alumno.categoria_nombre !== filtros.categoria) return false;
      if (filtros.curso && alumno.curso !== filtros.curso) return false;
      if (filtros.colegio && alumno.colegio !== filtros.colegio) return false;
      if (filtros.poblacion && alumno.poblacion !== filtros.poblacion) return false;

      return true;
    });

    // Ordenar: categoría (Minis→Prebenjamín→Benjamín→Alevín→Infantil), grupo y alfabético
    return filtrados.sort((a, b) => {
      const difCat = indiceCategoria(a.categoria_nombre) - indiceCategoria(b.categoria_nombre);
      if (difCat !== 0) return difCat;
      const difGrupo = (a.categoria_nombre || '').localeCompare(b.categoria_nombre || '', 'es');
      if (difGrupo !== 0) return difGrupo;
      const difApellidos = (a.apellidos || '').localeCompare(b.apellidos || '', 'es');
      if (difApellidos !== 0) return difApellidos;
      return (a.nombre || '').localeCompare(b.nombre || '', 'es');
    });
  }, [alumnos, filtros]);

  const actualizarFiltro = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const limpiarFiltros = () => {
    setFiltros({
      busqueda: '',
      categoria: '',
      curso: '',
      colegio: '',
      poblacion: ''
    });
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <MainNavbar />
      
      {/* Header - Solo visible en pantalla */}
      <div className="bg-white shadow print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Panel de Pagos</h1>
              <p className="text-gray-600">Control de pagos mensuales para impresión</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-700">
                {alumnosFiltrados.length !== alumnos.length ? (
                  <>
                    Mostrando: <span className="font-semibold text-blue-600">{alumnosFiltrados.length}</span> de <span className="font-semibold">{alumnos.length}</span>
                  </>
                ) : (
                  <>
                    Total alumnos: <span className="font-semibold">{alumnos.length}</span>
                  </>
                )}
              </span>
              <button
                onClick={handlePrint}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a1 1 0 001-1v-4a1 1 0 00-1-1H9a1 1 0 00-1 1v4a1 1 0 001 1zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                <span>Imprimir</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 print:px-0 print:py-2">
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg border border-red-300 print:hidden">
            {error}
          </div>
        )}

        {/* Filtros - Solo visible en pantalla */}
        <div className="bg-white rounded-lg shadow mb-6 print:hidden">
          <div className="p-4">
            {/* Búsqueda principal */}
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar por nombre, apellidos, DNI, teléfono..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={filtros.busqueda}
                    onChange={(e) => actualizarFiltro('busqueda', e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setMostrarFiltros(!mostrarFiltros)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                  </svg>
                  Filtros
                </button>
                <button
                  onClick={limpiarFiltros}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Limpiar
                </button>
              </div>
            </div>

            {/* Filtros avanzados */}
            {mostrarFiltros && (
              <div className="border-t pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Filtro por categoría */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                    <select
                      value={filtros.categoria}
                      onChange={(e) => actualizarFiltro('categoria', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Todas las categorías</option>
                      {valoresUnicos.categorias.map(categoria => (
                        <option key={categoria} value={categoria}>{categoria}</option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro por curso */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Curso</label>
                    <select
                      value={filtros.curso}
                      onChange={(e) => actualizarFiltro('curso', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Todos los cursos</option>
                      {valoresUnicos.cursos.map(curso => (
                        <option key={curso} value={curso}>{curso}</option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro por colegio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Colegio</label>
                    <select
                      value={filtros.colegio}
                      onChange={(e) => actualizarFiltro('colegio', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Todos los colegios</option>
                      {valoresUnicos.colegios.map(colegio => (
                        <option key={colegio} value={colegio}>{colegio}</option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro por población */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Población</label>
                    <select
                      value={filtros.poblacion}
                      onChange={(e) => actualizarFiltro('poblacion', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Todas las poblaciones</option>
                      {valoresUnicos.poblaciones.map(poblacion => (
                        <option key={poblacion} value={poblacion}>{poblacion}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Título para impresión */}
        <div className="hidden print:block text-center mb-4">
          <h1 className="text-xl font-bold">CONTROL DE PAGOS MENSUALES</h1>
          <p className="text-sm text-gray-600">Escuela Deportiva - {new Date().getFullYear()}</p>
        </div>

        {/* Tabla de Pagos */}
        <div className="bg-white rounded-lg shadow print:shadow-none print:rounded-none">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 print:text-xs">
              <thead className="bg-gray-50 print:bg-white">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 print:border-black print:py-2 w-1/4">
                    Datos del Alumno
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 print:border-black print:py-2 w-1/4">
                    Diciembre<br />50€
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300 print:border-black print:py-2 w-1/4">
                    Noviembre<br />50€
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider print:border-black print:py-2 w-1/4">
                    Octubre<br />120€
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 print:divide-black">
                {alumnosFiltrados.map((alumno, index) => (
                  <tr key={alumno.id} className="print:break-inside-avoid" style={{ minHeight: '120px' }}>
                    {/* Columna Datos del Alumno */}
                    <td className="px-3 py-4 border-r border-gray-300 print:border-black print:py-3 align-top">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900 print:text-xs">
                          {alumno.nombre} {alumno.apellidos}
                        </div>
                        <div className="text-sm text-gray-600 print:text-xs">
                          {alumno.categoria_nombre}
                        </div>
                        <div className="text-xs text-gray-500 print:text-xs">
                          Tel: {alumno.padre_telefono}
                        </div>
                        <div className="mt-2 space-y-1 text-xs">
                          <div className="flex">
                            <span className="w-8">Oct:</span>
                            <span className="border-b border-gray-400 flex-1 print:border-black"></span>
                          </div>
                          <div className="flex">
                            <span className="w-8">Nov:</span>
                            <span className="border-b border-gray-400 flex-1 print:border-black"></span>
                          </div>
                          <div className="flex">
                            <span className="w-8">Dic:</span>
                            <span className="border-b border-gray-400 flex-1 print:border-black"></span>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Columna Diciembre */}
                    <td className="px-3 py-4 border-r border-gray-300 print:border-black print:py-3 align-top text-center">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900 print:text-xs">
                          {alumno.nombre} {alumno.apellidos}
                        </div>
                        <div className="text-sm text-gray-600 print:text-xs">
                          {alumno.categoria_nombre}
                        </div>
                        <div className="text-sm font-bold text-green-600 print:text-xs">
                          50€
                        </div>
                        <div className="mt-4">
                          <div className="text-xs mb-1">Fecha:</div>
                          <div className="border-b border-gray-400 h-6 print:border-black"></div>
                        </div>
                      </div>
                    </td>

                    {/* Columna Noviembre */}
                    <td className="px-3 py-4 border-r border-gray-300 print:border-black print:py-3 align-top text-center">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900 print:text-xs">
                          {alumno.nombre} {alumno.apellidos}
                        </div>
                        <div className="text-sm text-gray-600 print:text-xs">
                          {alumno.categoria_nombre}
                        </div>
                        <div className="text-sm font-bold text-blue-600 print:text-xs">
                          50€
                        </div>
                        <div className="mt-4">
                          <div className="text-xs mb-1">Fecha:</div>
                          <div className="border-b border-gray-400 h-6 print:border-black"></div>
                        </div>
                      </div>
                    </td>

                    {/* Columna Octubre */}
                    <td className="px-3 py-4 print:py-3 align-top text-center">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900 print:text-xs">
                          {alumno.nombre} {alumno.apellidos}
                        </div>
                        <div className="text-sm text-gray-600 print:text-xs">
                          {alumno.categoria_nombre}
                        </div>
                        <div className="text-sm font-bold text-red-600 print:text-xs">
                          120€
                        </div>
                        <div className="mt-4">
                          <div className="text-xs mb-1">Fecha:</div>
                          <div className="border-b border-gray-400 h-6 print:border-black"></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mensaje si no hay resultados */}
        {alumnosFiltrados.length === 0 && (
          <div className="text-center py-8 print:hidden">
            <p className="text-gray-500">No se encontraron alumnos con los filtros aplicados.</p>
          </div>
        )}
      </div>

      {/* Estilos de impresión */}
      <style jsx>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 0.5cm;
          }
          
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .print\\:break-inside-avoid {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          
          table {
            border-collapse: collapse;
          }
          
          th, td {
            border: 1px solid black !important;
          }
          
          .print\\:border-black {
            border-color: black !important;
          }
          
          .print\\:text-xs {
            font-size: 10px !important;
            line-height: 1.2 !important;
          }
          
          .print\\:py-2 {
            padding-top: 4px !important;
            padding-bottom: 4px !important;
          }
          
          .print\\:py-3 {
            padding-top: 6px !important;
            padding-bottom: 6px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PagosDashboard;
